import React, { useState } from 'react';
import { useCertificateList } from '@/hooks/queries/certificate/useList';
import { useCertificateCreate, useCertificateUpdate, useCertificateDelete } from '@/hooks/mutations/certificate';
import { CertificateType, CertificateInputType } from '@/types/certificate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, ShieldCheck, FileText } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { CourseSelect } from '@/components/CourseSelect';
import { CourseCell } from '@/components/CourseCell';
import { getMediaUrl } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const CertificatesPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState<CertificateType | null>(null);

  const [formData, setFormData] = useState<CertificateInputType>({
    template: 'DEFAULT',
    file: '',
    thumbnail: '',
    courseId: '',
    translations: [
      { locale: 'uz', title: '', description: '' },
      { locale: 'ru', title: '', description: '' },
      { locale: 'en', title: '', description: '' },
    ],
  });

  const { data: certsData, isLoading, refetch } = useCertificateList({ pageNumber: page, pageSize: 20 });
  const { mutate: createCert, isPending: isCreating } = useCertificateCreate();
  const { mutate: updateCert, isPending: isUpdating } = useCertificateUpdate();
  const { mutate: deleteCert } = useCertificateDelete();

  const handleOpenCreate = () => {
    setSelectedCert(null);
    setFormData({
      template: 'DEFAULT',
      file: '',
      thumbnail: '',
      courseId: '',
      translations: [
        { locale: 'uz', title: '', description: '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (cert: CertificateType) => {
    setSelectedCert(cert);
    setFormData({
      template: cert.template || 'DEFAULT',
      file: cert.file || '',
      thumbnail: cert.thumbnail || '',
      courseId: cert.courseId || '',
      translations: [
        { locale: 'uz', title: cert.title || '', description: cert.description || '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'title' | 'description', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCert) {
      updateCert(
        { id: selectedCert.id, data: formData },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Sertifikat shabloni yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Sertifikat yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createCert(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi sertifikat shabloni yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Sertifikat yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedCert) return;
    deleteCert(selectedCert.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Sertifikat o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedCert(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Sertifikat o'chirilmadi", variant: 'destructive' }),
    });
  };

  const certsList: CertificateType[] = certsData?.data?.data || certsData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Sertifikat Shablonlari</h1>
          <p className="text-sm text-slate-500 mt-1">Kurslar uchun avtomatik PDF generatsiya qilinuvchi sertifikatlar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi shablon
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : certsList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <ShieldCheck className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha sertifikat shablonlari mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Fayl / Prevyu</th>
                  <th className="px-4 py-3.5">Nomi</th>
                  <th className="px-4 py-3.5">Kurs</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {certsList.map((cert) => (
                  <tr key={cert.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      {cert.thumbnail ? (
                        <img src={getMediaUrl(cert.thumbnail)} alt={cert.title} className="h-10 w-14 object-cover rounded border border-slate-200" />
                      ) : (
                        <div className="h-10 w-14 rounded bg-slate-100 text-slate-400 flex items-center justify-center">
                          <FileText className="size-5" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{cert.title || '—'}</td>
                    <td className="px-4 py-3">
                      <CourseCell courseId={cert.courseId} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(cert)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedCert(cert);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={certsData?.meta?.pagination ?? certsData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedCert ? 'Sertifikat shablonini tahrirlash' : 'Yangi sertifikat shabloni'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <CourseSelect
                label="Kurs"
                value={formData.courseId}
                onChange={(id) => setFormData({ ...formData, courseId: id })}
                required={true}
              />
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Shablon Kodi</Label>
                <Input
                  placeholder="DEFAULT"
                  value={formData.template}
                  onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                  className="rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileUploadInput
                label="Sertifikat Fayli (PDF / SVG / Rasm)"
                accept=".pdf,image/*"
                value={formData.file}
                onChange={(url) => setFormData({ ...formData, file: url })}
              />
              <FileUploadInput
                label="Thumbnail Prevyu (Rasm)"
                value={formData.thumbnail || ''}
                onChange={(url) => setFormData({ ...formData, thumbnail: url })}
              />
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { title: '', description: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Sertifikat nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="Node.js Backend Sertifikati"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Tavsif ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.description || ''}
                          onChange={(v) => handleTranslationChange(locale, 'description', v)}
                          placeholder="Kursni muvaffaqiyatli tamomlaganlik to'g'risida..."
                          minHeight={80}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Sertifikat shablonini o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu shablon tizimdan butunlay o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default CertificatesPage;
